"use client";
import { Slider } from "@/components/ui/slider";
import wallet from "@/assets/images/icons/empty-wallet.svg";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import { toolsListIconsCostCalculator } from "@/utils/cost-icons";
import gsap from "gsap";
import { BookDemoHubspotButtonUrl } from "@/utils/hubspot-contants";
import CustomButton from "@/components/common/CustomButton";
import { FaCheckCircle, FaCross } from "react-icons/fa";

type TselectedApp = {
  toolName: string;
  toolIcon: string;
  toolId: string;
};

const titleStyle = ` text-[20px] sm:text-[22px] md:text-[26px] xl:text-[28px] font-normal`;

const CostCalculator = () => {
  const [rangeValue, setRangeValue] = useState<number[]>([10]);
  const [selectedApps, setSelectedApps] = useState<TselectedApp[]>(
    toolsListIconsCostCalculator.length > 0
      ? [toolsListIconsCostCalculator[0]]
      : [],
  );

  const [saveAmount, setSaveAmount] = useState(0);
  const appCountRef = useRef(null);
  const saveAmountRef = useRef(null);

  const handleSelectedApps = (item: TselectedApp) => {
    const foundedApp = selectedApps.find((app) => app.toolId === item.toolId);
    let updatedApps;

    if (foundedApp) {
      updatedApps =
        selectedApps.length > 1
          ? selectedApps.filter((app) => app.toolId !== item.toolId)
          : selectedApps;
    } else {
      updatedApps = [...selectedApps, item];
    }

    setSelectedApps(updatedApps);
  };

  useEffect(() => {
    const newSaveAmount = selectedApps.length * rangeValue[0] * 65;
    setSaveAmount(newSaveAmount);
  }, [selectedApps, rangeValue]);

  useEffect(() => {
    if (appCountRef.current && saveAmountRef.current) {
      gsap.to(appCountRef.current, {
        innerText: selectedApps.length,
        duration: 0.5,
        snap: { innerText: 1 },
        ease: "power2.out",
      });

      gsap.to(saveAmountRef.current, {
        innerText: saveAmount,
        duration: 0.5,
        snap: { innerText: 1 },
        ease: "power2.out",
      });
    }
  }, [selectedApps.length, saveAmount]);

  return (
    <section className="py-[60px] xl:py-[80px]">
      <div className="container">
        <div className="mx-auto flex max-w-[780px] flex-col items-center gap-y-9">
          <div className="flex items-center gap-4 xl:gap-6">
            <div className="relative rounded-[8px] from-[#781E8F] to-[#422CBB] before:absolute before:bottom-[-1px] before:left-[-1px] before:right-[-1px] before:top-[-1px] before:z-[-1] before:rounded-[9px] before:bg-gradient-to-r">
              <div className="h-full overflow-hidden rounded-[8px] bg-white">
                <div className="flex h-20 w-20 items-center justify-center rounded-[8px] xl:p-3">
                  <Image src={wallet} alt="Cost savings - Kroolo AI tools pricing calculator" />
                </div>
              </div>
            </div>
            <div>
              <p className="text-[26px] font-normal text-[#202020] xl:text-[28px]">
                Save costs
              </p>
              <p className="text-[20px] font-normal text-[#717171]">
                Do more with less
              </p>
            </div>
          </div>
        </div>
        <div className="relative mt-[40px] w-full rounded-[8px] xl:mt-12">
          <div className="h-full overflow-hidden rounded-[8px] bg-gradient-to-b from-[#F4F4FD] to-[#fefefe] px-5 py-4 xl:px-12">
            <p className="text-[16px] font-normal text-[#202020] lg:text-[18px]">
              How many employees do you have?
            </p>
            <div className="my-6">
              <Slider
                defaultValue={rangeValue}
                max={500}
                step={1}
                onValueChange={(value) => setRangeValue(value)}
                ariaLabel="Choose a value"
              />
            </div>
            <div className="flex items-center justify-between">
              {[0, 100, 200, 300, 400, 500].map((item, index) => (
                <p key={index}> {item} </p>
              ))}
            </div>
          </div>
        </div>
        <div className="relative rounded-[8px] bg-gradient-to-b to-[#F4F4FD] from-[#ffffff]">
          <div className="h-full overflow-hidden rounded-[8px]">
            <div className="grid grid-cols-1 items-center lg:grid-cols-2 xlg:items-start xl:items-center">
              <div className="h-full px-4 py-5 xl:px-8 xl:py-7">
                <p className="text-[16px] font-normal text-[#202020] lg:text-[18px]">What tools are you using?</p>
                <div className="my-4 grid grid-cols-4 gap-3 sm:my-8 sm:gap-5 xl:mb-8 xl:mt-12 xl:grid-cols-5 xl:gap-14">
                  {toolsListIconsCostCalculator.map((item, index) => {
                    const ids = selectedApps.map((item) => item.toolId);
                    const selected = ids.includes(item.toolId);
                    return (
                      <div
                        className="flex cursor-pointer flex-col items-center gap-y-2"
                        key={index}
                        onClick={() => handleSelectedApps(item)}
                        role="button"
                        aria-pressed={selected}
                        aria-label={`Select ${item.toolName} tool`}
                      >
                        <div
                          className={`relative flex h-12 w-12 items-center justify-center rounded-[8px] backdrop-blur-[90px] xl:h-16 xl:w-16 ${selected
                            ? "bg-gradient-to-r from-[#781E8F] to-[#422CBB] before:absolute before:bottom-[-1px] before:left-[-1px] before:right-[-1px] before:top-[-1px] before:z-[-1] before:rounded-[9px] before:bg-gradient-to-r"
                            : "border-none"
                            } `}
                        >
                          {selected && (
                            <div className="absolute right-0 top-0 rounded-full bg-white p-[2px] text-black">
                              <FaCheckCircle className="h-4 w-4" />
                            </div>
                          )}
                          <div
                            className={`h-full overflow-hidden rounded-[8px] bg-white ${selected ? "p-[5px]" : "p-1"
                              } `}
                          >
                            <Image
                              className="h-full w-full object-cover"
                              src={item.toolIcon}
                              alt={`${item.toolName} - Kroolo AI cost calculator tool`}
                              quality={100}
                            />
                          </div>
                        </div>
                        <p className="text-[14px] font-normal text-[#202020] lg:text-[16px]">
                          {item.toolName}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="h-full space-y-4 rounded-r-[8px] px-6 py-6 xl:space-y-8 xl:px-[64px]">
                <div className="space-y-2 xl:space-y-4">
                  <p className={titleStyle}>
                    <span ref={appCountRef}>{selectedApps.length}</span>{" "}
                    Apps{" "}
                  </p>
                  <CommonSubtitle
                    color="#202020"
                    fontSizeWeight="text-[14px] lg:text-[16px] font-normal"
                  >
                    It is estimated that Kroolo can replace at least 3 of your
                    existing business tools.
                  </CommonSubtitle>
                </div>
                <div className="space-y-2 xl:space-y-4">
                  <p className={`${titleStyle} font-semibold`}>
                    {" "}
                    Save&nbsp;
                    <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                      $<span ref={saveAmountRef}>{saveAmount}</span>/year
                    </span>
                  </p>
                  <CommonSubtitle
                    color="#202020"
                    fontSizeWeight="text-[14px] lg:text-[16px] font-normal"
                  >
                    {`For a company of ${rangeValue[0]} employees, Kroolo can save a
                total cost of Upto $${saveAmount} per year.`}
                  </CommonSubtitle>
                </div>
                <CustomButton
                  text="Request Demo"
                  url={BookDemoHubspotButtonUrl}
                  variant="primary"
                  version="black"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;
